// `loadAuth`: picks and starts the auth module for this server (`/api/info.auth`). The Privy
// island is a dynamic import, so its code (React + Privy) is fetched only here, never on the
// free-play path. Resolves null when sign-in is unavailable (older server, island failed to
// load); the UI then hides the sign-in button and money stays "coming soon".
//
// Contracts step: complete (the island itself is the client builder's).

import type { AuthInfo, AuthModule } from './auth.js';
import { createFakeAuth, type FakeAuthDeps } from './fake.js';

export interface LoadAuthDeps extends FakeAuthDeps {
  /** Server base (net/server-url.ts). */
  readonly baseUrl: string;
  readonly warn?: (msg: string, err: unknown) => void;
}

let cached: { key: string; module: Promise<AuthModule | null> } | null = null;

export function loadAuth(info: AuthInfo | null, deps: LoadAuthDeps): Promise<AuthModule | null> {
  if (info === null) return Promise.resolve(null);
  const key = `${info.provider}:${info.privyAppId}:${deps.baseUrl}`;
  if (cached?.key === key) return cached.module;
  const module: Promise<AuthModule | null> =
    info.provider === 'fake'
      ? Promise.resolve(createFakeAuth({ ...deps, http: { ...deps.http, baseUrl: deps.baseUrl } }))
      : import('./privy-island.js')
          .then((m) => m.createPrivyAuth({ appId: info.privyAppId, baseUrl: deps.baseUrl }))
          .catch((err: unknown) => {
            (deps.warn ?? ((msg, e) => console.warn(msg, e)))('ball.io: sign-in unavailable', err);
            cached = null;
            return null;
          });
  cached = { key, module };
  return module;
}

/** Forgets the cached module (tests). */
export function resetAuthCache(): void {
  cached = null;
}
